// The plain-text rules behind Clyde's "needs your input" items: does a question
// name a held term, what day a buyer's text asks for, and a one-letter typo
// match. Pure — the console's resolve card and the resolve-order service share them.
import { addCalendarDays } from "./fulfillmentDates";
import { weekdayOfCalendarDate } from "./periods";

/** Lower-case, accents and punctuation off, one space between words. */
export function plainWords(text: string): string[] {
  return text
    .normalize("NFKD")
    .replace(/[̀-ͯ]/g, "")
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, " ")
    .trim()
    .split(" ")
    .filter(Boolean);
}

// "beets" / "beet", "tomatoes" / "tomato", "berries" / "berry".
function singular(word: string): string {
  if (word.length > 4 && word.endsWith("ies")) return `${word.slice(0, -3)}y`;
  if (word.length > 4 && word.endsWith("oes")) return word.slice(0, -2);
  if (word.length > 3 && word.endsWith("s") && !word.endsWith("ss")) return word.slice(0, -1);
  return word;
}

/** At most one letter added, dropped or changed ("beats" ~ "beets", "brocoli" ~ "broccoli"). */
export function withinOneEdit(a: string, b: string): boolean {
  if (a === b) return true;
  if (Math.abs(a.length - b.length) > 1) return false;
  let i = 0;
  let j = 0;
  let edits = 0;
  while (i < a.length && j < b.length) {
    if (a[i] === b[j]) {
      i++;
      j++;
      continue;
    }
    if (++edits > 1) return false;
    if (a.length > b.length) i++;
    else if (b.length > a.length) j++;
    else {
      i++;
      j++;
    }
  }
  return edits + (a.length - i) + (b.length - j) <= 1;
}

/**
 * Two words are the same word: equal once plurals are off, or — for words of five
 * letters or more — one typo apart. Short words must match exactly ("pea" is not
 * "pear").
 */
export function sameWord(a: string, b: string): boolean {
  const x = singular(a);
  const y = singular(b);
  if (x === y) return true;
  // Before and after the plural comes off: "beats" ~ "beets" are five letters
  // apart by one, though "beat" / "beet" are only four.
  return (Math.min(a.length, b.length) >= 5 && withinOneEdit(a, b))
    || (Math.min(x.length, y.length) >= 5 && withinOneEdit(x, y));
}

// Words that carry no product meaning in a term ("2 cs of the beets").
const FILLER = new Set(["a", "an", "the", "of", "and", "or", "some", "more", "cs", "case", "cases", "box", "boxes", "bag", "bags", "lb", "lbs", "each", "ea", "x"]);

/** The words of a held term that name the product — numbers and pack words off. */
export function termWords(term: string): string[] {
  return plainWords(term).filter((w) => !FILLER.has(w) && !/^\d+$/.test(w));
}

/**
 * The text names the term: every product word of the term appears in it as a word
 * (plurals and one typo forgiven, see sameWord). A term with no product words
 * never matches — "2 cs" names nothing.
 */
export function textNamesTerm(text: string, term: string): boolean {
  const wanted = termWords(term);
  if (wanted.length === 0) return false;
  const words = plainWords(text);
  return wanted.every((w) => words.some((v) => sameWord(v, w)));
}

const WEEKDAYS: Array<[number, RegExp]> = [
  [0, /^(sun|sunday)$/],
  [1, /^(mon|monday)$/],
  [2, /^(tue|tues|tuesday)$/],
  [3, /^(wed|weds|wednesday)$/],
  [4, /^(thu|thur|thurs|thursday)$/],
  [5, /^(fri|friday)$/],
  [6, /^(sat|saturday)$/],
];
const TODAY = /^(today|tonight|tonite)$/;
const TOMORROW = /^(tomorrow|tomorow|tommorow|tommorrow|tmrw|tmr|tmw|tmrow|tomoro)$/;

/**
 * The day a buyer's text asks for, worked out from the day it was sent (on the
 * org's calendar): "today", "tomorrow" / "tmrw", or a weekday — the next one after
 * the sending day ("Friday" sent on a Friday is next week's). Null when the text
 * names no day, or names two different ones.
 */
export function dateFromBuyerText(text: string, sentOn: string): string | null {
  const sentWeekday = weekdayOfCalendarDate(sentOn);
  if (sentWeekday === null) return null;
  const found = new Set<string>();
  for (const word of plainWords(text)) {
    if (TODAY.test(word)) found.add(sentOn);
    else if (TOMORROW.test(word)) found.add(addCalendarDays(sentOn, 1));
    else {
      const day = WEEKDAYS.find(([, re]) => re.test(word));
      if (day) found.add(addCalendarDays(sentOn, ((day[0] - sentWeekday + 6) % 7) + 1));
    }
  }
  return found.size === 1 ? Array.from(found)[0] : null;
}
